/**
 * App-root fabric host: renders the active spotlight with driver.js.
 * driver.js is used for OVERLAY POSITIONING ONLY (design doc §4.5) — the
 * coach-mark styling is ours (fabric.css .fabric-coach). Subscribing to the
 * registry map makes the spotlight reactive: a target that mounts AFTER the
 * directive (the navigate -> board-mount race) lights up the moment its
 * data-guide-id registers. No timers, no retries.
 */
import { useEffect } from 'react'
import { driver } from 'driver.js'
import 'driver.js/dist/driver.css'
import './fabric.css'

import { useFabricGuideStore } from '@/stores/fabricGuide'
import { useFabricRegistryStore } from '@/stores/fabricRegistry'

/**
 * Escape to inert text. The coach-mark is authored by the LLM and driver.js
 * renders popover.description via innerHTML, so an unescaped coachMark is an
 * XSS sink (a prompt-injected `<img onerror=…>` would execute in the nurse's
 * browser). The textContent→innerHTML round-trip neutralises all HTML; the
 * coach-mark is only ever a plain sentence, so nothing legitimate is lost.
 */
function escapeHtml(s: string): string {
  const el = document.createElement('div')
  el.textContent = s
  return el.innerHTML
}

export function FabricGuideHost() {
  const active = useFabricGuideStore((s) => s.active)
  const dismiss = useFabricGuideStore((s) => s.dismiss)
  // Depend on WHETHER the active target is currently registered — NOT on the
  // whole entries Map. The Map changes on ANY registration anywhere (e.g.
  // Home's per-agent registrations), which would otherwise tear down and
  // recreate the driver.js overlay mid-guidance. This boolean flips only when
  // the active target itself mounts/unmounts, so the reactive-mount behaviour
  // (light up when the target appears) is preserved while unrelated churn is
  // ignored.
  const targetPresent = useFabricRegistryStore((s) =>
    active ? active.guideIds.some((id) => s.entries.has(id)) : false,
  )

  useEffect(() => {
    if (!active) return
    const targetId = active.guideIds.find((id) =>
      document.querySelector(`[data-guide-id="${CSS.escape(id)}"]`),
    )
    if (!targetId) return // target not mounted yet — re-runs on registry change
    const d = driver({
      showButtons: [],
      stagePadding: 6,
      overlayOpacity: 0.4,
      popoverClass: 'fabric-coach',
      onDestroyStarted: () => {
        // User closed the overlay (tap outside / Esc): destroy + report.
        d.destroy()
        dismiss()
      },
    })
    d.highlight({
      element: `[data-guide-id="${CSS.escape(targetId)}"]`,
      popover: { description: escapeHtml(active.coachMark) },
    })
    return () => {
      if (d.isActive()) d.destroy()
    }
  }, [active, targetPresent, dismiss])

  return null
}
